import React from 'react';
import { Globe, LayoutDashboard, Building2, Wrench, Check, ArrowRight } from 'lucide-react';
import { useInView } from 'react-intersection-observer';
import Section from './Section';

const Services: React.FC = () => {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const services = [
    {
      icon: Globe,
      title: 'LP・Webサイト制作',
      description: '集客やブランディングのためのWebサイト。スマホ対応・SEO対策込み。',
      features: ['レスポンシブ対応', 'SEO基本設定', 'お問い合わせフォーム', 'SNS連携'],
      price: '10〜30万円',
      period: '1〜2週間',
    },
    {
      icon: LayoutDashboard,
      title: '業務Webアプリ開発',
      description: 'Excel作業をシステム化。顧客管理、在庫管理、シフト管理など。',
      features: ['業務フロー分析', 'データベース設計', 'スマホからも利用可能', '既存データの移行'],
      price: '30〜100万円',
      period: '1〜2ヶ月',
    },
    {
      icon: Building2,
      title: '基幹システム開発',
      description: '受発注・請求・在庫を一元管理。会社全体の業務効率を大幅に改善。',
      features: ['要件定義・設計', '複数部門連携', '帳票・レポート出力', '段階的な導入支援'],
      price: '100〜300万円',
      period: '2〜4ヶ月',
    }
  ];

  return (
    <Section id="services" className="bg-[#F5F3EF]" ref={ref}>
      {/* フルワイド画像 */}
      <div className="relative h-[180px] sm:h-[260px] lg:h-[340px] overflow-hidden">
        <img
          src="/images/services.webp"
          alt=""
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#F5F3EF]" />
      </div>

      <div className={`mx-auto max-w-6xl px-5 lg:px-6 pb-20 pt-8 lg:pb-28 lg:pt-12 transition-all duration-1000 ${
        inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
      }`}>
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 rounded-full border border-[#B8860B]/30 bg-[#B8860B]/10 px-4 py-1.5 mb-6">
            <span className="text-sm font-semibold text-[#B8860B]">サービス内容</span>
          </div>
          <h2 className="text-3xl font-bold lg:text-4xl leading-tight text-[#2D3748]">
            あなたのビジネスに合った<br />
            <span className="text-gradient">解決策があります</span>
          </h2>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {services.map((service, index) => {
            const Icon = service.icon;
            return (
              <div
                key={index}
                className="rounded-2xl border border-[#E8E6E1] bg-white p-6 lg:p-8 shadow-sm transition-all duration-300 hover:shadow-md hover:-translate-y-1"
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#1B2A4A]/10 mb-5">
                  <Icon className="h-6 w-6 text-[#1B2A4A]" />
                </div>

                <h3 className="text-xl font-bold text-[#2D3748] mb-2">
                  {service.title}
                </h3>

                <p className="text-[#5A6577] leading-relaxed mb-4 text-justify">
                  {service.description}
                </p>

                {/* 価格帯 */}
                <div className="inline-flex items-center gap-3 rounded-full bg-[#1B2A4A] pl-5 pr-6 py-2.5 mb-5">
                  <span className="text-xl lg:text-2xl font-bold text-white">{service.price}</span>
                  <span className="text-xs text-white/60">{service.period}</span>
                </div>

                <ul className="space-y-2.5">
                  {service.features.map((feature, fIndex) => (
                    <li key={fIndex} className="flex items-center gap-2.5 text-[#5A6577]">
                      <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#2D8659]/10">
                        <Check className="h-3 w-3 text-[#2D8659]" />
                      </div>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        {/* 保守プランバナー */}
        <div className="mt-8 rounded-2xl border border-[#E8E6E1] bg-white p-6 shadow-sm">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#1B2A4A]">
                <Wrench className="h-5 w-5 text-white" />
              </div>
              <div>
                <span className="text-lg font-bold text-[#2D3748]">月額保守プラン </span>
                <span className="text-lg font-bold text-[#B8860B]">月1〜5万円</span>
              </div>
            </div>
            <p className="text-[#5A6577] text-sm">サーバー管理・セキュリティ更新・軽微な修正・相談し放題</p>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-10">
          <a
            href="#contact"
            className="group inline-flex items-center justify-center gap-2 rounded-lg bg-[#B8860B] px-7 py-3.5 text-white font-semibold hover:bg-[#A0750A] transition-colors shadow-lg shadow-[#B8860B]/30"
          >
            どのプランが合うか相談する（無料）
            <ArrowRight className="h-4 w-4 transform group-hover:translate-x-1 transition-transform" />
          </a>
        </div>
      </div>
    </Section>
  );
};

export default Services;
